/**
 * Verlof API Client (Spoor 2, Verlof MVP)
 *
 * AD-9: typed client per resource, no inline apiRequest calls in
 * components. AD-4: `/me` never accepts a client-controlled employeeId —
 * this client intentionally exposes no such parameter.
 */

import { apiRequest } from './api';
import type {
  ApprovalQueueItem,
  DecideLeaveCorrectionResult,
  KoppelReden,
  LeaveHistoryEntry,
  MaandruimteStand,
  OwnLeaveOverview,
  RequestStatus,
  SelectableEmployee,
  SubmitLeaveCorrectionResult,
  SubmitLeaveRequestResult,
  SyncStatus,
  TeamOnbevestigdeUrenOverzicht,
  TeamVerlofRegel,
  VerlofAfsluitenOverzicht,
  Verlofsoort,
  ZodCreateLeaveRequestInput,
  ZodSubmitLeaveCorrectionInput,
} from '@functieloonhuis/shared';

export interface DecideLeaveRequestResult {
  id: number;
  requestStatus: RequestStatus;
  balanceWarning: { hours: number } | null;
}

export interface RetrySyncResult {
  id: number;
  syncStatus: SyncStatus;
  syncError: string | null;
}

/** Story 2.2 (Epic 2, Verlof correctie): uitkomst van het terugzoeken van het kenmerk voor één regel. */
export interface LinkKenmerkResult {
  requestId: number;
  gekoppeld: boolean;
  reden: KoppelReden;
}

export const verlofApi = {
  getMyOverview: () => apiRequest<{ data: OwnLeaveOverview }>('/leave-requests/me'),

  /**
   * Vóór wie mag de ingelogde gebruiker verlof invoeren? Scope komt volledig
   * van de server (AD-4) — er is bewust geen parameter om iets anders te
   * vragen dan waar je recht op hebt.
   */
  getSelectableEmployees: () =>
    apiRequest<{ data: SelectableEmployee[] }>('/leave-requests/selectable-employees'),

  /**
   * Story 6.2: de stand van de maandruimte voor de maand waarin `datum` valt.
   *
   * `uren` zijn de uren van de aanvraag in wording. Ze gaan mee zodat de
   * SERVER vaststelt of die aanvraag boven de grens uitkomt (KE-5); het scherm
   * vergelijkt geen getallen en telt niets op. `employeeId` alleen bij een
   * namens-aanvraag — de eigen stand vraag je zonder dat veld.
   */
  getMaandruimte: (datum: string, uren?: number, employeeId?: number) => {
    const query = new URLSearchParams({ datum });
    if (uren !== undefined) query.set('uren', String(uren));
    if (employeeId !== undefined) query.set('employeeId', String(employeeId));
    return apiRequest<{ data: MaandruimteStand | null }>(
      `/leave-requests/maandruimte?${query.toString()}`
    );
  },

  submit: (input: ZodCreateLeaveRequestInput) =>
    apiRequest<{ data: SubmitLeaveRequestResult }>('/leave-requests', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  withdraw: (id: number) =>
    apiRequest<{ data: { withdrawn: true } }>(`/leave-requests/${id}/withdraw`, {
      method: 'POST',
    }),

  /**
   * Story 3.1: "Ja, dat klopt" — de eigen uren bevestigen (AC-3). Geen body:
   * de scope komt volledig van de server (AD-4), dus er valt niets mee te
   * sturen dat er iets aan verandert.
   */
  confirmHours: (id: number) =>
    apiRequest<{ data: { confirmed: true } }>(`/leave-requests/${id}/confirm-hours`, {
      method: 'POST',
    }),

  /**
   * Story 2.10: de historie van één verlofaanvraag (FR-16) — wie corrigeerde
   * wat, wanneer, en of het doorzetten naar de loonadministratie slaagde. De
   * server bepaalt of je hem mag zien (de medewerker zelf, zijn leidinggevende
   * of HR); er is bewust geen parameter om iets anders te vragen.
   */
  getLeaveHistory: (id: number) =>
    apiRequest<{ data: LeaveHistoryEntry[] }>(`/leave-requests/${id}/history`),

  /** Story 3.1: goedkeuringslijst met beslis-context (V4). */
  getApprovalQueue: () =>
    apiRequest<{ data: ApprovalQueueItem[] }>('/leave-requests/approval-queue'),

  /** Story 3.2: besluit nemen (goedkeuren/afwijzen). `reason` is verplicht bij `decision: 'reject'` (AC 2). */
  decide: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    apiRequest<{ data: DecideLeaveRequestResult }>(`/leave-requests/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, reason }),
    }),

  /**
   * Story 7.3: het HR-besluit op een aanvraag die bij HR ligt. Een eigen pad
   * naast `decide` hierboven, want het is een ander besluit op een andere as
   * (AD-29): dit schrijft de HR-kolommen en laat het besluit van de eerste
   * beslisser staan. `reason` is verplicht bij `decision: 'reject'` (AC-7).
   */
  hrDecide: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    apiRequest<{ data: { id: number; requestStatus: string } }>(`/leave-requests/${id}/hr-decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, reason }),
    }),

  /**
   * Story 7.7: de leidinggevende neemt zijn goedkeuring terug zolang HR nog
   * niet besliste (FR-30). Geen reden mee: §10.5 kent er geen veld voor.
   */
  retractApproval: (id: number) =>
    apiRequest<{ data: { id: number; requestStatus: string } }>(
      `/leave-requests/${id}/retract-approval`,
      { method: 'POST' }
    ),

  /**
   * Story 7.8: HR stelt de einddatum vast zolang de aanvraag op zijn besluit
   * wacht (FR-31). De begindatum gaat met opzet niet mee — die verandert niet.
   */
  updateEndDate: (id: number, endDate: string) =>
    apiRequest<{ data: { id: number; endDate: string; hours: number } }>(
      `/leave-requests/${id}/end-date`,
      { method: 'PATCH', body: JSON.stringify({ endDate }) }
    ),

  /**
   * Story 5.5: HR corrigeert een verkeerd gekozen soort zolang de aanvraag nog
   * niet is besloten (FR-2). De server herschrijft in dezelfde handeling de
   * boek-as en de HR-route; het scherm stuurt alleen de nieuwe soort.
   */
  corrigeerVerlofsoort: (id: number, verlofsoort: Verlofsoort) =>
    // De route geeft ook de herschreven `hr_stap_reden` terug; die staat hier
    // bewust NIET in het type. Sinds story 8.2 (AC-9) leidt géén enkel scherm
    // de route of de stand nog zelf af: de server stuurt `ligtBij` en `stand`
    // mee en het scherm leest die velden (KE-11). Een reden die hier zou staan
    // is vroeg of laat een veld dat iemand alsnog met de status gaat
    // combineren, en dat is precies de tweede afleiding die AC-9 wegneemt.
    apiRequest<{
      data: { id: number; verlofsoort: Verlofsoort; syncStatus: string; gewijzigd: boolean };
    }>(`/leave-requests/${id}/verlofsoort`, {
      method: 'PATCH',
      body: JSON.stringify({ verlofsoort }),
    }),

  /** Story 3.3: retry een mislukte (of nog niet-geboekte) Nmbrs-boeking. */
  retrySync: (id: number) =>
    apiRequest<{ data: RetrySyncResult }>(`/leave-requests/${id}/retry-sync`, {
      method: 'POST',
    }),

  /**
   * Story 2.2 (Epic 2, Verlof correctie): kenmerk terugzoeken voor één regel.
   * Voorbereidend -- deze story levert geen scherm; de knop *Opnieuw
   * koppelen* (UX Deel D2, blok ③) hoort bij story 4.2 en hoeft dan alleen
   * deze methode aan te roepen. Alleen lezen aan de kant van de
   * loonadministratie (FR-6), dus herhaalbaar zonder risico.
   */
  linkKenmerk: (id: number) =>
    apiRequest<{ data: LinkKenmerkResult }>(`/nmbrs/leave/request/${id}/link`, {
      method: 'POST',
    }),

  /**
   * Story 2.4: urencorrectie indienen op een reeds goedgekeurde aanvraag.
   * `nmbrs_leave_requests` zelf verandert hier niet — dat gebeurt pas na een
   * goedgekeurde correctie (Story 2.6).
   */
  submitCorrection: (id: number, input: ZodSubmitLeaveCorrectionInput) =>
    apiRequest<{ data: SubmitLeaveCorrectionResult }>(`/leave-requests/${id}/corrections`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Story 2.6: een ingediende urencorrectie beoordelen. `id` is het
   * CORRECTIE-id (`leave_hour_corrections.id`), niet het aanvraag-id — vandaar
   * het eigen `/corrections/`-pad naast `decide` hierboven.
   * `reason` is verplicht bij `decision: 'reject'` (AC-7).
   */
  decideCorrection: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    apiRequest<{ data: DecideLeaveCorrectionResult }>(`/leave-requests/corrections/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, reason }),
    }),

  /**
   * Story 11.1, AC-7: het HR-besluit op een urencorrectie die bij HR ligt.
   * Een EIGEN eindpunt naast `decideCorrection` hierboven, want het schrijft
   * de HR-kolommen en laat die van de eerste beslisser met rust (AD-29).
   */
  decideCorrectieHrStap: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    apiRequest<{ data: DecideLeaveCorrectionResult }>(
      `/leave-requests/corrections/${id}/hr-decide`,
      {
        method: 'POST',
        body: JSON.stringify({ decision, reason }),
      }
    ),

  /**
   * Story 4.2: de HR-pagina *Verlof afsluiten*. Alleen de maand gaat mee — het
   * bereik in medewerkers komt volledig van de server (AD-4).
   */
  getVerlofAfsluiten: (maand: string) =>
    apiRequest<{ data: VerlofAfsluitenOverzicht }>(`/hr/verlof-afsluiten?maand=${maand}`),

  /**
   * Story 2.3: de koppelactie over de hele historie, vanaf de HR-pagina. Leest
   * alleen aan de kant van de loonadministratie (FR-6) en is dus herhaalbaar.
   */
  linkAlleKenmerken: () =>
    apiRequest<{ data: { gekoppeld: number; nietGekoppeld: number; uitgevoerdOp: string } }>(
      '/nmbrs/leave/link-all',
      { method: 'POST' }
    ),

  /**
   * Story 4.3: het blok *Uren nog niet bevestigd* van het eigen team. Geen
   * enkele parameter: wie welk team ziet bepaalt de server (AD-4).
   */
  getOnbevestigdeUren: () =>
    apiRequest<{ data: TeamOnbevestigdeUrenOverzicht }>('/leave-requests/onbevestigde-uren'),

  /**
   * Story 8.4: het verlof van het eigen team, als LEESLIJST. Geen enkele
   * parameter: wie welk team ziet bepaalt de server (AD-4).
   */
  getTeamVerlof: () => apiRequest<{ data: TeamVerlofRegel[] }>('/leave-requests/team-verlof'),
};
